import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAtom, useAtomValue } from 'jotai';
import { guildsAtom, selGuildAtom, GuildOption } from '../atoms/guild';

export function GuildAvatar({ guild, size = 6 }: { guild: GuildOption; size?: number }) {
  const px = size * 4;
  if (guild.icon) {
    return (
      <img
        src={guild.icon}
        alt={guild.name}
        className="rounded-full object-cover shrink-0"
        style={{ width: px, height: px }}
      />
    );
  }
  const initials = guild.name
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <span
      className="rounded-full shrink-0 flex items-center justify-center text-[10px] font-black text-slate-950 select-none"
      style={{ width: px, height: px, background: 'linear-gradient(135deg,#00ff66,#00c8a8)' }}
    >
      {initials}
    </span>
  );
}

export function GuildSelector() {
  const guilds            = useAtomValue(guildsAtom);
  const [selId, setSelId] = useAtom(selGuildAtom);
  const [open, setOpen]   = useState(false);
  const btnRef            = useRef<HTMLButtonElement>(null);
  const dropRef           = useRef<HTMLDivElement>(null);
  const [pos, setPos]     = useState({ top: 0, left: 0, width: 0 });

  const openDropdown = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, left: r.left, width: Math.max(r.width, 220) });
    }
    setOpen(v => !v);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || dropRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selected = guilds.find(g => g.id === selId) ?? guilds[0];

  if (guilds.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/[0.03] border border-white/[0.07] text-[10px] font-mono text-slate-500">
        <i className="fa-solid fa-server text-slate-600 text-[10px]" />
        No servers
      </div>
    );
  }

  return (
    <>
      <button
        ref={btnRef}
        onClick={openDropdown}
        className="flex items-center gap-2.5 pl-2 pr-3 py-1.5 rounded-xl border transition-all select-none min-w-[160px] max-w-[220px]"
        style={{
          background: open ? 'rgba(0,255,102,0.06)' : 'rgba(255,255,255,0.03)',
          borderColor: open ? 'rgba(0,255,102,0.25)' : 'rgba(255,255,255,0.08)',
        }}
      >
        {selected && <GuildAvatar guild={selected} size={5} />}
        <span className="text-[11px] font-semibold text-slate-200 flex-1 truncate text-left">
          {selected?.name ?? 'Select server'}
        </span>
        <i className={`fa-solid fa-chevron-down text-[9px] text-slate-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && createPortal(
        <div
          ref={dropRef}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            minWidth: pos.width,
            maxWidth: 260,
            zIndex: 99999,
            background: 'linear-gradient(180deg, #0e1220 0%, #090c14 100%)',
            border: '1px solid rgba(0,255,102,0.12)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.8), 0 0 0 1px rgba(0,255,102,0.06), 0 0 24px rgba(0,255,102,0.05)',
            backdropFilter: 'blur(20px)',
            borderRadius: 16,
            overflow: 'hidden',
            paddingTop: 6,
            paddingBottom: 6,
          }}
        >
          <div style={{ padding: '4px 12px 8px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            <span style={{ fontSize: 8, fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#475569' }}>
              Discord Servers
            </span>
          </div>

          {guilds.map(guild => {
            const isActive = guild.id === selId;
            return (
              <button
                key={guild.id}
                onClick={() => { setSelId(guild.id); setOpen(false); }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  textAlign: 'left',
                  position: 'relative',
                  background: isActive ? 'rgba(0,255,102,0.07)' : 'transparent',
                  color: isActive ? '#00ff66' : '#94a3b8',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => {
                  if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.04)';
                }}
                onMouseLeave={e => {
                  if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                }}
              >
                {isActive && (
                  <div style={{
                    position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)',
                    width: 2, height: 20, background: '#00ff66',
                    borderRadius: '0 2px 2px 0', boxShadow: '0 0 6px #00ff66',
                  }} />
                )}
                <GuildAvatar guild={guild} size={6} />
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isActive ? '#00ff66' : '#e2e8f0' }}>
                    {guild.name}
                  </span>
                  <span style={{ fontSize: 9, fontFamily: 'monospace', color: '#334155', marginTop: 2 }}>
                    {(guild.members ?? 0).toLocaleString()} members
                  </span>
                </div>
                {isActive && (
                  <div style={{ marginLeft: 'auto', width: 6, height: 6, borderRadius: '50%', background: '#00ff66', boxShadow: '0 0 8px #00ff66', flexShrink: 0 }} />
                )}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}
